import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle } from 'lucide-react';
import Header from './components/Header6';
import Footer from './components/Footer6';

const Contact6 = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        setTimeout(() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }, 3000);
    };

    const inputStyle = {
        width: '100%', padding: '14px 16px', backgroundColor: '#FAF7F2', border: '1px solid #D4C5B0', borderRadius: '2px',
        color: '#2C1810', fontFamily: "'Nunito', sans-serif", fontSize: '14px', outline: 'none', transition: 'border-color 0.3s',
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');
                .dharti-contact-input:focus { border-color: #8B4513 !important; }
            `}</style>
            <div style={{ fontFamily: "'Nunito', -apple-system, sans-serif" }} className="min-h-screen bg-[#F5F0E8]">
                <Header />

                {/* Hero */}
                <section className="pt-[120px] pb-12 lg:pb-16" style={{ borderBottom: '1px solid #E8DFD1' }}>
                    <div className="max-w-3xl mx-auto text-center px-5">
                        <p className="text-xs font-semibold tracking-[3px] uppercase mb-4" style={{ color: '#8B4513' }}>Get In Touch</p>
                        <h1 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-4xl lg:text-6xl font-normal text-[#2C1810] mb-6 tracking-wide">Contact Us</h1>
                        <p className="text-[#7C6B5D] text-base lg:text-lg" style={{ fontWeight: 300, lineHeight: 1.7 }}>
                            We would love to hear from you. Whether you have questions about our handloom collection, custom orders, or artisan collaborations.
                        </p>
                    </div>
                </section>

                <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12 lg:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                        {/* Form */}
                        <div className="lg:col-span-2">
                            <div style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8DFD1', borderRadius: '4px', padding: '32px' }}>
                                <h2 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '22px', fontWeight: 600, color: '#2C1810', marginBottom: '24px' }}>Send Us a Message</h2>
                                {submitted ? (
                                    <div className="text-center py-12">
                                        <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#8B4513', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                            <Send size={24} style={{ color: '#FFFFFF' }} />
                                        </div>
                                        <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '20px', color: '#2C1810', marginBottom: '8px' }}>Message Sent!</h3>
                                        <p style={{ fontSize: '14px', color: '#7C6B5D', fontWeight: 300 }}>We will get back to you within 24 hours.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <input name="name" value={formData.name} onChange={handleInputChange} placeholder="Your Name" required style={inputStyle} className="dharti-contact-input" />
                                            <input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address" required style={inputStyle} className="dharti-contact-input" />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="Phone (Optional)" style={inputStyle} className="dharti-contact-input" />
                                            <input name="subject" value={formData.subject} onChange={handleInputChange} placeholder="Subject" required style={inputStyle} className="dharti-contact-input" />
                                        </div>
                                        <textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Your Message" required rows={5} style={{ ...inputStyle, resize: 'vertical' }} className="dharti-contact-input" />
                                        <button type="submit" className="inline-flex items-center gap-2" style={{ padding: '16px 36px', backgroundColor: '#8B4513', color: '#FFFFFF', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>
                                            <Send size={16} /> Send Message
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-6">
                            {[
                                { icon: MapPin, title: 'Visit Our Studio', lines: ['42 Heritage Lane, Amer Road', 'Jaipur, Rajasthan 302002'] },
                                { icon: Phone, title: 'Call Us', lines: ['+91 98765 43210', 'Mon-Sat, 10AM - 7PM'] },
                                { icon: Mail, title: 'Email Us', lines: ['hello@dharti.com', 'orders@dharti.com'] },
                                { icon: Clock, title: 'Working Hours', lines: ['Monday - Saturday: 10AM - 7PM', 'Sunday: Closed'] },
                            ].map((item, i) => (
                                <div key={i} style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8DFD1', borderRadius: '4px', padding: '24px' }}>
                                    <div className="flex items-start gap-4">
                                        <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: 'rgba(139,69,19,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <item.icon size={20} style={{ color: '#8B4513' }} />
                                        </div>
                                        <div>
                                            <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '16px', fontWeight: 600, color: '#2C1810', marginBottom: '8px' }}>{item.title}</h3>
                                            {item.lines.map((line, j) => (
                                                <p key={j} style={{ fontSize: '14px', color: '#7C6B5D', margin: '0 0 2px 0', fontWeight: 300 }}>{line}</p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Contact6;
